import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useProductStore } from "./store/productStore";
import { useCartStore } from "./store/cartStore";
import ProductCard from "./ProductCard";
import ProductSkeleton from "./components/ProductSkeleton";
import QuickViewModal from "./components/QuickViewModal";
import Tooltip from "./components/Tooltip";
import { SearchIcon, getCategoryIcon } from "./components/CategoryIcons";
import { CloseIcon } from "./components/NavIcons";

export default function Products() {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const products = useProductStore((state) => state.products);
  const categories = useProductStore((state) => state.categories);
  const fetchProducts = useProductStore((state) => state.fetchProducts);
  const fetchCategories = useProductStore((state) => state.fetchCategories);
  const loading = useProductStore((state) => state.loading);
  const error = useProductStore((state) => state.error);
  const addItem = useCartStore((state) => state.addItem);


  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Initial fetch and Filter changes
  useEffect(() => {
    // If we need to filter by category but don't have categories yet, wait.
    if (selectedCategory !== "all" && categories.length === 0) {
      return;
    }

    // If selectedCategory is "all", we don't need to re-run when categories change.
    // However, hooks run on any dependency change.
    // We can't easily detect "what changed", but checking usage helps.

    const fetchInitialData = async () => {
      setPage(1);
      setHasMore(true);

      const params = { 
        limit: 24,
        page: 1 
      };
      
      if (searchTerm) params.search = searchTerm;
      if (selectedCategory !== "all") {
        const cat = categories.find(c => c.id === parseInt(selectedCategory));
        if (cat) {
            params.category = cat.name;
        } else {
            // Category selected but not found (legacy link?), fallback or just don't filter?
            // For now, let's just not set the category param if not found, 
            // effectively showing all or handling as "all".
            console.warn("Selected category ID not found in categories list:", selectedCategory);
        }
      }
      
      try {
        const data = await fetchProducts(params, false);
        if (data) {
           setHasMore(data.length >= 24);
        }
      } catch (err) {
          console.error("Fetch initial data failed:", err);
      }
    };

    const timeoutId = setTimeout(fetchInitialData, searchTerm ? 500 : 0);
    return () => clearTimeout(timeoutId);
  }, [fetchProducts, searchTerm, selectedCategory, categories]);

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    const params = { 
      limit: 24,
      page: nextPage
    };
    
    if (searchTerm) params.search = searchTerm;
    if (selectedCategory !== "all") {
      const cat = categories.find(c => c.id === parseInt(selectedCategory));
      if (cat) params.category = cat.name;
    }
    
    const data = await fetchProducts(params, true); // append
    
    if (data) {
       setPage(nextPage);
       if (data.length < 24) {
           setHasMore(false);
       }
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Remove client-side filtering
  const filtered = products;

  const handleAddToCart = (product) => {
    addItem(product);
  };



  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-xl text-red-600 dark:text-red-400">
          {t('products.error', { message: error })}
        </p>
        <pre className="mt-4 p-4 bg-raya-card rounded text-left text-xs overflow-auto max-w-2xl mx-auto">
          {JSON.stringify({
             message: error,
             time: new Date().toISOString(),
             apiUrl: "https://raya-business-app-worker.getachew-abey.workers.dev/api"
          }, null, 2)}
        </pre>
      </div>
    );
  }

  if (loading && products.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-xl text-raya-text-secondary">{t('products.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-raya-accent to-raya-accent-hover bg-clip-text text-transparent">
          {t('products.title')}
        </h1>

        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Enhanced Search Input */}
          <div className="flex-1 max-w-2xl">
            <Tooltip content="Search products by name or description">
              <div className="relative group">
                {/* Search Icon */}
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-raya-text-tertiary group-focus-within:text-raya-accent transition-colors">
                  <SearchIcon className="w-5 h-5" />
                </div>
                
                {/* Input */}
                <input
                  type="text"
                  placeholder={t('products.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-12 pl-12 pr-12 bg-raya-bg-off dark:bg-raya-card backdrop-blur-xl border border-raya-border rounded-xl
                    focus:outline-none focus:ring-2 focus:ring-raya-accent focus:border-raya-accent 
                    transition-all duration-300
                    placeholder:text-raya-text-tertiary
                    shadow-sm hover:shadow-md focus:shadow-lg text-raya-text-primary"
                />
                
                {/* Clear Button */}
                {searchTerm && (
                  <Tooltip content="Clear search">
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-raya-text-tertiary hover:text-raya-accent transition-all duration-200 hover:rotate-90"
                    >
                      <CloseIcon className="w-5 h-5" />
                    </button>
                  </Tooltip>
                )}
              </div>
            </Tooltip>
          </div>
          
          {/* Category Filter */}
          <div className="w-full md:w-auto">
            {/* Mobile Dropdown */}
            <div className="relative block md:hidden w-full">
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className="w-full flex justify-between items-center gap-2 h-12 px-4 
                  bg-raya-bg/70 dark:bg-raya-bg/80 backdrop-blur-xl 
                  border border-raya-border/50 rounded-xl
                  focus:outline-none focus:ring-2 focus:ring-raya-accent/50 
                  transition-all duration-300 shadow-sm hover:shadow-md"
              >
                <div className="flex items-center gap-2">
                  {(() => {
                    const CategoryIcon = getCategoryIcon(
                      selectedCategory === "all" 
                        ? "all" 
                        : categories.find(c => c.id === parseInt(selectedCategory))?.name
                    );
                    return <CategoryIcon className="w-5 h-5 text-raya-accent" />;
                  })()}
                  <span className="font-medium">
                    {selectedCategory === "all"
                      ? t('products.allCategories', { defaultValue: 'All' })
                      : (() => {
                          const cat = categories.find(c => c.id === parseInt(selectedCategory));
                          return cat ? (i18n.language === 'am' && cat.name_am ? cat.name_am : cat.name) : t('products.allCategories');
                        })()
                    }
                  </span>
                </div>
                <svg 
                  className={`w-5 h-5 transition-transform ${isCategoryOpen ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isCategoryOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 w-full 
                  bg-raya-bg/95 dark:bg-raya-bg/95 backdrop-blur-xl
                  border border-raya-border rounded-xl shadow-xl z-50 
                  max-h-80 overflow-y-auto">
                  <button
                    onClick={() => {
                      setSelectedCategory("all");
                      setIsCategoryOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-raya-accent-light transition border-b border-raya-border ${
                      selectedCategory === "all" ? "bg-raya-accent-light text-raya-accent" : ""
                    }`}
                  >
                    {(() => {
                      const CategoryIcon = getCategoryIcon("all");
                      return <CategoryIcon className="w-5 h-5" />;
                    })()}
                    <span className="font-medium">{t('products.allCategories', { defaultValue: 'All' })}</span>
                  </button>
                  {categories.map((cat) => {
                    const CategoryIcon = getCategoryIcon(cat.name);
                    return (
                      <button
                        key={cat.id}
                        onClick={() => {
                          setSelectedCategory(cat.id);
                          setIsCategoryOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-raya-accent-light transition border-b border-raya-border last:border-0 ${
                          selectedCategory === cat.id ? "bg-raya-accent-light text-raya-accent" : ""
                        }`}
                      >
                        <CategoryIcon className="w-5 h-5" />
                        <span className="font-medium">{i18n.language === 'am' && cat.name_am ? cat.name_am : cat.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Desktop Pills */}
            <div className="hidden md:flex flex-wrap gap-3">
              <Tooltip content={`All Products`}>
                <button
                  onClick={() => setSelectedCategory("all")}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl whitespace-nowrap transition-all duration-300 transform hover:scale-105 ${
                    selectedCategory === "all"
                      ? "bg-gradient-to-r from-raya-accent to-raya-accent-hover text-raya-bg shadow-lg hover:shadow-xl"
                      : "bg-raya-bg/70 dark:bg-raya-bg/80 backdrop-blur-xl border border-raya-border/50 hover:bg-raya-accent-light hover:border-raya-accent/50 shadow-sm hover:shadow-md"
                  }`}
                >
                  {(() => {
                    const CategoryIcon = getCategoryIcon("all");
                    return <CategoryIcon className="w-5 h-5" />;
                  })()}
                  <span className="font-medium">{t('products.allCategories', { defaultValue: 'All' })}</span>
                </button>
              </Tooltip>
              {categories.map((cat) => {
                const CategoryIcon = getCategoryIcon(cat.name);
                const categoryName = i18n.language === 'am' && cat.name_am ? cat.name_am : cat.name;
                return (
                  <Tooltip key={cat.id} content={`${categoryName}`}>
                    <button
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`flex items-center gap-2 px-5 py-3 rounded-xl whitespace-nowrap transition-all duration-300 transform hover:scale-105 ${
                        selectedCategory === cat.id
                          ? "bg-gradient-to-r from-raya-accent to-raya-accent-hover text-raya-bg shadow-lg hover:shadow-xl"
                          : "bg-raya-bg/70 dark:bg-raya-bg/80 backdrop-blur-xl border border-raya-border/50 hover:bg-raya-accent-light hover:border-raya-accent/50 shadow-sm hover:shadow-md"
                      }`}
                    >
                      <CategoryIcon className="w-5 h-5" />
                      <span className="font-medium">{categoryName}</span>
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>

        {loading && products.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <ProductSkeleton key={i} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-raya-text-secondary">
              {searchTerm 
                ? t('products.noResults', { searchTerm }) 
                : t('home.noProducts')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filtered.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
                onQuickView={setQuickViewProduct}
              />
            ))}
          </div>
        )}

        {/* Load More Button (All Devices) */}
        {hasMore && filtered.length > 0 && (
          <div className="flex justify-center mt-8 pb-4">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="px-8 py-3 bg-gradient-to-r from-raya-accent to-raya-accent-hover text-raya-bg rounded-full font-semibold shadow-lg hover:shadow-xl active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{t('products.loading', 'Loading...')}</span>
                </>
              ) : (
                <span>{t('products.loadMore', 'Load More')}</span>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Single Shared Quick View Modal */}
      <QuickViewModal
        product={quickViewProduct}
        isOpen={!!quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
        onAddToCart={handleAddToCart}
      />
    </div>
  );
}
